/**
 * <registration-form>
 *
 * Multi-step wholesale lead capture form rendered on the dedicated
 * registration page (/pages/cadastro — sections/registration-page.liquid).
 * It talks to the App Proxy route /apps/shop/shop/wholesale-registration
 * in three stages that mirror the server contract:
 *
 *   Step 1 ("Avançar")  — e-mail + telefone + first-touch UTMs.
 *                         Upserts the customer (tags wholesale-registration).
 *   Step 2 ("Avançar")  — Pessoa Física (nome, CPF, nascimento) ou Pessoa
 *                         Jurídica (nome, razão social, CNPJ, inscrição
 *                         estadual, com opção de isenção). Tags PF/PJ.
 *   Step 3 ("Finalizar")— experiência no ramo + canais + Instagram/site.
 *                         Promotes the customer to atacado/pending-approval.
 *
 * Each network step only advances the UI once the server responds ok.
 *
 * The current step is mirrored into the URL (?registration_step=N) via
 * pushState, so the browser back button walks the steps; popstate re-syncs the
 * UI. A leftover step param on a fresh page load is stripped (the form state
 * only lives in the DOM, so deep-linking into a later step has no data).
 */
class RegistrationForm extends HTMLElement {
	static ENDPOINT = '/apps/shop/shop/wholesale-registration'
	// Single site-wide UTM store, written by analytics-tracker.js on any page
	// (snake_case keys, first-touch — never overwritten once set). The form only
	// READS it; it holds the lead's original attribution.
	static UTM_KEY = 'arvenne_utms'
	// Legacy form-only store, now unified into UTM_KEY. Removed on load to clear
	// stale first-touch values left in browsers from the old code.
	static LEGACY_UTM_KEY = 'arvenne_wholesale_utms'
	static STEP_PARAM = 'registration_step'
	static STEP_NUMBERS = { form: 1, profile: 2, revenda: 3 }
	static STEP_NAMES = { 1: 'form', 2: 'profile', 3: 'revenda' }
	// Same business number used across the theme (footer, floating button, PDP).
	static WHATSAPP_NUMBER = '5511990078496'

	constructor() {
		super()

		this.captureUtms()
		this.steps = this.querySelectorAll('[data-reg-step]')
		this.typeInputs = this.querySelectorAll('input[name="registration_type"]')

		// Step 1 — telefone com seletor de país (<phone-input>); expõe e164/isComplete
		this.phoneInput = this.querySelector('phone-input')

		// Step 2 — Pessoa Física fields
		this.nameField = this.querySelector('[data-reg-name-field]')
		this.cpfField = this.querySelector('[data-reg-cpf-field]')
		this.birthdateField = this.querySelector('[data-reg-birthdate-field]')
		this.nameInput = this.nameField && this.nameField.querySelector('input')
		this.cpfInput = this.cpfField && this.cpfField.querySelector('input')
		this.birthdateInput = this.birthdateField && this.birthdateField.querySelector('input')

		// Step 2 — Pessoa Jurídica fields
		this.pjNameField = this.querySelector('[data-reg-pj-name-field]')
		this.razaoField = this.querySelector('[data-reg-razao-field]')
		this.cnpjField = this.querySelector('[data-reg-cnpj-field]')
		this.ieGroup = this.querySelector('[data-reg-ie-group]')
		this.pjNameInput = this.pjNameField && this.pjNameField.querySelector('input')
		this.razaoInput = this.razaoField && this.razaoField.querySelector('input')
		this.cnpjInput = this.cnpjField && this.cnpjField.querySelector('input')
		this.ieInput = this.querySelector('[data-reg-ie]')
		this.ieIsento = this.querySelector('[data-reg-ie-isento]')

		// Step 3 — investimento inicial só aparece para quem ainda não começou
		this.experienceInputs = this.querySelectorAll('input[name="experience"]')
		this.investmentField = this.querySelector('[data-reg-investment-field]')
		this.investmentInputs = this.querySelectorAll('input[name="investment"]')

		// Step 3 — reveal Instagram/site only for the matching channels
		this.canalInputs = this.querySelectorAll('input[name="canal"]')
		this.instagramField = this.querySelector('[data-reg-instagram-field]')
		this.siteField = this.querySelector('[data-reg-site-field]')
		this.instagramInput = this.instagramField && this.instagramField.querySelector('input')
		this.siteInput = this.siteField && this.siteField.querySelector('input')
		this.proofHint = this.querySelector('[data-reg-proof-hint]')

		// "Já tem conta? Entrar" — proactive shortcut, also highlighted when the
		// server reports the e-mail already belongs to an account.
		this.signin = this.querySelector('[data-reg-signin]')
		this.signinLink = this.querySelector('[data-reg-signin-link]')

		// Success step — WhatsApp confirmation CTA
		this.whatsappLink = this.querySelector('[data-reg-whatsapp]')
		this.whatsappCountdown = this.querySelector('[data-reg-countdown]')
		this.whatsappProgressTrack = this.querySelector('[data-reg-whatsapp-progress]')
		this.whatsappProgressFill = this.querySelector('[data-reg-whatsapp-progress-fill]')
		this.whatsappTimer = null

		this.currentStep = 'form'
		this.maxStepReached = 1

		this.bindEvents()
		this.syncTipoFields()
		this.syncChannels()
		this.syncInvestment()
		this.normalizeStepParam()
	}

	bindEvents() {
		this.typeInputs.forEach((input) =>
			input.addEventListener('change', () => this.syncTipoFields()),
		)

		// Live masks (o telefone é mascarado dentro do próprio <phone-input>)
		this.bindMask(this.cpfInput, this.maskCPF)
		this.bindMask(this.cnpjInput, this.maskCNPJ)
		this.bindMask(this.birthdateInput, this.maskDate)
		this.bindMask(this.instagramInput, this.maskInstagram)

		// Clear error styling as the user fixes a field
		this.querySelectorAll('input').forEach((input) => {
			input.addEventListener('input', () => this.clearFieldError(input))
		})

		if (this.ieIsento) {
			this.ieIsento.addEventListener('change', () => this.syncIsento())
		}

		this.querySelectorAll('[data-reg-next]').forEach((button) => {
			button.addEventListener('click', (event) => {
				event.preventDefault()
				const current = button.closest('[data-reg-step]')
				const stepName = current ? current.dataset.regStep : null
				if (!this.validateStep(stepName)) return

				if (button.hasAttribute('data-reg-submit')) {
					this.submit(button)
				} else {
					this.goToStep(button.dataset.regNext, 'push')
				}
			})
		})

		this.querySelectorAll('[data-reg-back]').forEach((button) => {
			button.addEventListener('click', (event) => {
				event.preventDefault()
				this.goBack(button.dataset.regBack)
			})
		})

		if (this.whatsappLink) {
			this.whatsappLink.addEventListener('click', () => this.clearWhatsAppTimer())
		}

		this.querySelectorAll('[data-reg-close]').forEach((button) => {
			button.addEventListener('click', (event) => {
				event.preventDefault()
				this.clearWhatsAppTimer()
				// Registration done — send the user back to the storefront.
				window.location.href = '/'
			})
		})

		// Browser back/forward walks the steps via the URL param.
		window.addEventListener('popstate', this.onPopstate)

		// Clear choice-group errors as soon as the user makes a selection
		this.bindGroupClear('input[name="experience"]', '[data-reg-experience]')
		this.bindGroupClear('input[name="investment"]', '[data-reg-investment]')
		this.bindGroupClear('input[name="canal"]', '[data-reg-canal]')

		// Reveal the investment range only for "Ainda não comecei"
		this.experienceInputs.forEach((input) => {
			input.addEventListener('change', () => this.syncInvestment())
		})

		// Reveal Instagram/site fields based on the selected channels
		this.canalInputs.forEach((input) => {
			input.addEventListener('change', () => this.syncChannels())
		})
	}

	bindGroupClear(inputSelector, groupSelector) {
		this.querySelectorAll(inputSelector).forEach((input) => {
			input.addEventListener('change', () => {
				const group = this.querySelector(groupSelector)
				if (group) group.classList.remove('registration-form__options--error')
			})
		})
	}

	/** Instagram shows for the "instagram" channel, site for "ecommerce". */
	syncChannels() {
		const checked = Array.from(this.canalInputs)
			.filter((c) => c.checked)
			.map((c) => c.value)

		const showInstagram = checked.includes('instagram')
		const showSite = checked.includes('ecommerce')
		this.toggleField(this.instagramField, this.instagramInput, showInstagram, false)
		this.toggleField(this.siteField, this.siteInput, showSite, false)

		// Social-proof nudge: only meaningful once one of the fields is on screen.
		if (this.proofHint) this.proofHint.hidden = !(showInstagram || showSite)

		// The handle always starts with "@" — seed it when the field appears so
		// the user never has to type the character themselves.
		if (showInstagram && this.instagramInput && !this.instagramInput.value) {
			this.instagramInput.value = '@'
		}
	}

	/**
	 * "Quanto pretende investir pra começar?" only applies to a lead who hasn't
	 * started reselling yet ("Ainda não comecei"). Reveal + enable it for that
	 * answer, hide + reset (and clear any error) for every other.
	 */
	syncInvestment() {
		const show = this.querySelector('input[name="experience"]:checked')?.value === 'nao_comecei'

		if (this.investmentField) {
			const wasHidden = this.investmentField.hidden
			this.investmentField.hidden = !show
			if (show && wasHidden) this.revealField(this.investmentField)
		}

		this.investmentInputs.forEach((input) => {
			input.disabled = !show
			if (!show) input.checked = false
		})

		if (!show) {
			const group = this.querySelector('[data-reg-investment]')
			if (group) group.classList.remove('registration-form__options--error')
		}
	}

	bindMask(input, maskFn) {
		if (!input) return
		input.addEventListener('input', () => {
			input.value = maskFn(input.value)
		})
	}

	get selectedType() {
		const checked = this.querySelector('input[name="registration_type"]:checked')
		return checked ? checked.value : 'fisica'
	}

	/** Show the PF fields for Pessoa Física, the PJ fields for Pessoa Jurídica. */
	syncTipoFields() {
		const isFisica = this.selectedType === 'fisica'

		this.toggleField(this.nameField, this.nameInput, isFisica)
		this.toggleField(this.cpfField, this.cpfInput, isFisica)
		this.toggleField(this.birthdateField, this.birthdateInput, isFisica)
		this.toggleField(this.pjNameField, this.pjNameInput, !isFisica)
		this.toggleField(this.razaoField, this.razaoInput, !isFisica)
		this.toggleField(this.cnpjField, this.cnpjInput, !isFisica)

		// Inscrição estadual: the whole group (field + isento checkbox) only
		// exists for Pessoa Jurídica; the input itself also disables while the
		// isento box is checked.
		if (this.ieGroup) {
			const wasHidden = this.ieGroup.hidden
			this.ieGroup.hidden = isFisica
			if (!isFisica && wasHidden) this.revealField(this.ieGroup)
		}
		if (this.ieIsento) {
			this.ieIsento.disabled = isFisica
			if (isFisica) this.ieIsento.checked = false
		}
		this.syncIsento()
	}

	/** "Isento de Inscrição Estadual" disables (and un-requires) the IE input. */
	syncIsento() {
		if (!this.ieInput) return
		const isJuridica = this.selectedType === 'juridica'
		const isento = this.isIsento()
		this.ieInput.disabled = !isJuridica || isento
		this.ieInput.required = isJuridica && !isento
		if (!isJuridica) this.ieInput.value = ''
		if (isento) this.clearFieldError(this.ieInput)
	}

	isIsento() {
		return !!(this.ieIsento && this.ieIsento.checked)
	}

	toggleField(field, input, show, requireWhenShown = true) {
		if (!field) return
		const wasHidden = field.hidden
		field.hidden = !show
		if (show && wasHidden) this.revealField(field)
		if (input) {
			input.disabled = !show
			input.required = show && requireWhenShown
			if (!show) {
				input.value = ''
				this.clearFieldError(input)
			}
		}
	}

	/** Replay the rise animation on a field a toggle just made visible. */
	revealField(el) {
		el.classList.remove('is-revealing')
		void el.offsetWidth
		el.classList.add('is-revealing')
		el.addEventListener('animationend', () => el.classList.remove('is-revealing'), { once: true })
	}

	validateStep(name) {
		if (name === 'form') return this.validateContact()
		if (name === 'profile') return this.validateProfile()
		if (name === 'revenda') return this.validateRevenda()
		return true
	}

	validateContact() {
		// Telefone: número completo com código do país e ao menos 14 caracteres
		// (ex.: "+5511999999999"). A checagem usa o estado do <phone-input>.
		return this.runChecks([
			{ el: this.querySelector('[data-reg-email]'), test: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) },
			{ el: this.phoneInput, test: () => this.phoneComplete() },
		])
	}

	phoneComplete() {
		return !!(this.phoneInput && this.phoneInput.isComplete)
	}

	phoneValue() {
		if (this.phoneInput && this.phoneInput.e164) return this.phoneInput.e164
		return this.val('[data-reg-phone]')
	}

	validateProfile() {
		const checks = []

		if (this.selectedType === 'fisica') {
			checks.push({ el: this.nameInput, test: (v) => v.trim().length >= 2 })
			checks.push({ el: this.cpfInput, test: (v) => this.onlyDigits(v).length === 11 })
			checks.push({ el: this.birthdateInput, test: (v) => this.isValidBirthdate(v) })
		} else {
			checks.push({ el: this.pjNameInput, test: (v) => v.trim().length >= 2 })
			checks.push({ el: this.razaoInput, test: (v) => v.trim().length >= 2 })
			checks.push({ el: this.cnpjInput, test: (v) => this.onlyDigits(v).length === 14 })
			if (!this.isIsento()) {
				checks.push({ el: this.ieInput, test: (v) => v.trim().length >= 1 })
			}
		}

		return this.runChecks(checks)
	}

	/** Marks failing fields, focuses the first one. True when all pass. */
	runChecks(checks) {
		let firstInvalid = null
		checks.forEach(({ el, test }) => {
			if (!el) return
			const valid = test(el.value || '')
			this.setFieldError(el, !valid)
			if (!valid && !firstInvalid) firstInvalid = el
		})
		if (firstInvalid) firstInvalid.focus()
		return !firstInvalid
	}

	/** dd/mm/aaaa, a real calendar date, not in the future. */
	isValidBirthdate(value) {
		const v = (value || '').trim()
		if (!/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return false
		const [day, month, year] = v.split('/').map(Number)
		const date = new Date(year, month - 1, day)
		return (
			year >= 1900 &&
			date.getFullYear() === year &&
			date.getMonth() === month - 1 &&
			date.getDate() === day &&
			date.getTime() <= Date.now()
		)
	}

	validateRevenda() {
		const naoComecei = this.querySelector('input[name="experience"]:checked')?.value === 'nao_comecei'
		const groups = [
			{ selector: '[data-reg-experience]', valid: !!this.querySelector('input[name="experience"]:checked') },
			{ selector: '[data-reg-canal]', valid: Array.from(this.canalInputs).some((c) => c.checked) },
		]

		// Required only when the lead hasn't started reselling yet.
		if (naoComecei) {
			groups.push({
				selector: '[data-reg-investment]',
				valid: !!this.querySelector('input[name="investment"]:checked'),
			})
		}

		let firstInvalid = null
		groups.forEach(({ selector, valid }) => {
			const group = this.querySelector(selector)
			if (!group) return
			group.classList.toggle('registration-form__options--error', !valid)
			if (!valid && !firstInvalid) firstInvalid = group
		})

		if (firstInvalid) {
			const input = firstInvalid.querySelector('input')
			if (input) input.focus()
			return false
		}
		return true
	}

	/**
	 * Resolve the first-touch UTM attribution for this registration. The
	 * site-wide store (arvenne_utms, owned by analytics-tracker.js) holds the
	 * lead's original attribution and wins; only when nothing was ever captured
	 * do we fall back to this page's URL. The form never writes its own store.
	 */
	captureUtms() {
		// snake_case (URL / arvenne_utms) → camelCase (backend Zod contract).
		const map = {
			utm_source: 'utmSource',
			utm_medium: 'utmMedium',
			utm_campaign: 'utmCampaign',
			utm_term: 'utmTerm',
			utm_content: 'utmContent',
		}
		const remap = (get) => {
			const out = {}
			Object.keys(map).forEach((key) => {
				const value = get(key)
				if (typeof value === 'string' && value.trim()) out[map[key]] = value.trim()
			})
			return out
		}

		// Drop the legacy form-only store (superseded by arvenne_utms).
		try {
			localStorage.removeItem(RegistrationForm.LEGACY_UTM_KEY)
		} catch (_) {
			/* storage unavailable */
		}

		// First-touch wins: prefer the lead's original attribution from the
		// site-wide store over the current page's URL.
		try {
			const raw = JSON.parse(localStorage.getItem(RegistrationForm.UTM_KEY) || '{}')
			const stored = remap((key) => raw[key])
			if (Object.keys(stored).length) {
				this.utms = stored
				return
			}
		} catch (_) {
			/* storage unavailable */
		}

		// Nothing captured yet (e.g. the lead's first ever touch is this page):
		// fall back to the UTM in this page's URL.
		const params = new URLSearchParams(window.location.search)
		this.utms = remap((key) => params.get(key))
	}

	val(selector) {
		const el = this.querySelector(selector)
		return el ? el.value.trim() : ''
	}

	getStep1Payload() {
		return this.prune({
			step: '1',
			email: this.val('[data-reg-email]'),
			telefone: this.phoneValue(),
			...this.utms,
			...this.readClickIds(),
			...this.readMetaIds(),
			...this.metaContext(),
		})
	}

	/**
	 * Google Ads click ids (gclid / gbraid / wbraid) saved by analytics-tracker.js
	 * on the visitor's last ad click. Sent with step 1 so the backend can store
	 * them on the lead and later import the sale into Google Ads.
	 */
	readClickIds() {
		try {
			const raw = JSON.parse(localStorage.getItem('arvenne_click_ids') || '{}')
			const out = {}
			;['gclid', 'gbraid', 'wbraid'].forEach((key) => {
				if (typeof raw[key] === 'string' && raw[key].trim()) out[key] = raw[key].trim()
			})
			return out
		} catch (_) {
			return {}
		}
	}

	/**
	 * Meta Conversions API matching ids. _fbc is built from the last fbclid saved
	 * by analytics-tracker.js (fb.1.<click ms>.<fbclid>), falling back to the
	 * _fbc cookie; _fbp is the Pixel's browser-id cookie. Both are optional.
	 */
	readMetaIds() {
		const out = {}
		try {
			const raw = JSON.parse(localStorage.getItem('arvenne_click_ids') || '{}')
			if (typeof raw.fbclid === 'string' && raw.fbclid.trim() && raw.fbclid_at) {
				out.fbc = `fb.1.${raw.fbclid_at}.${raw.fbclid.trim()}`
			}
		} catch (_) {
			/* storage unavailable */
		}
		// A malformed cookie makes decodeURIComponent throw, and this runs inside
		// the step 1 payload: an uncaught throw here would block the signup.
		const cookie = (name) => {
			try {
				const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
				return match ? decodeURIComponent(match[1]) : ''
			} catch (_) {
				return ''
			}
		}
		const fbc = cookie('_fbc')
		const fbp = cookie('_fbp')
		if (!out.fbc && fbc) out.fbc = fbc
		if (fbp) out.fbp = fbp
		return out
	}

	/** Browser context for server-side Meta events (the proxy hop hides it). */
	metaContext() {
		return { userAgent: navigator.userAgent, pageUrl: window.location.href }
	}

	getStep2Payload() {
		const email = this.val('[data-reg-email]')

		if (this.selectedType === 'fisica') {
			return this.prune({
				step: '2',
				email,
				tipoCadastro: 'PF',
				nome: this.val('[data-reg-name]'),
				cpf: this.val('[data-reg-cpf]'),
				dataNascimento: this.val('[data-reg-birthdate]'),
			})
		}

		return this.prune({
			step: '2',
			email,
			tipoCadastro: 'PJ',
			nome: this.val('[data-reg-pj-name]'),
			razaoSocial: this.val('[data-reg-razao]'),
			cnpj: this.val('[data-reg-cnpj]'),
			inscricaoEstadual: this.isIsento() ? 'ISENTO' : this.val('[data-reg-ie]'),
		})
	}

	getStep3Payload() {
		const canaisVenda = Array.from(this.canalInputs)
			.filter((c) => c.checked)
			.map((c) => c.value)

		// Only sent for "Ainda não comecei" (the radios are disabled otherwise).
		const investment = this.querySelector('input[name="investment"]:checked')

		const payload = this.prune({
			step: '3',
			email: this.val('[data-reg-email]'),
			experienciaRamo: this.querySelector('input[name="experience"]:checked')?.value || '',
			investimentoInicial: investment && !investment.disabled ? investment.value : '',
			// Only sent when their channel is selected (field visible/enabled).
			// A lone "@" prefix means nothing was typed, so treat it as empty.
			instagram: this.instagramHandle(),
			site: this.siteInput && !this.siteInput.disabled ? this.siteInput.value.trim() : '',
			anonymousId: this.anonymousId(),
			...this.metaContext(),
		})

		if (canaisVenda.length) payload.canaisVenda = canaisVenda
		return payload
	}

	anonymousId() {
		try {
			return localStorage.getItem('arvenne_anonymous_id') || ''
		} catch (_) {
			return ''
		}
	}

	/** Drop empty-string values so optional fields aren't sent blank. */
	prune(obj) {
		return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v != null))
	}

	/**
	 * True when the page was opened from the price-gate "complete seu cadastro"
	 * flow (logged-in customer without tags → ?hide_signin=1). Used to segment
	 * the signup funnel by origin.
	 */
	cameFromPriceGate() {
		try {
			return new URLSearchParams(location.search).get('hide_signin') === '1'
		} catch (_) {
			return false
		}
	}

	async submit(button) {
		const step = button.dataset.regSubmit
		let payload
		if (step === '3') payload = this.getStep3Payload()
		else if (step === '2') payload = this.getStep2Payload()
		else payload = this.getStep1Payload()
		const nextStep = button.dataset.regNext

		if (step === '1' && !this.signupStartedSent) {
			this.signupStartedSent = true
			if (typeof window.arvenneTrack === 'function') {
				window.arvenneTrack('signup_started', {
					location: 'registration_page',
					from_price_gate: this.cameFromPriceGate(),
				})
			}
		}

		this.setLoading(button, true)
		this.showError('', button)

		try {
			const response = await fetch(RegistrationForm.ENDPOINT, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
				body: JSON.stringify(payload),
			})

			let data = {}
			try {
				data = await response.json()
			} catch (_) {
				/* non-JSON response */
			}

			if (!response.ok || data.ok === false) {
				if (this.isAccountExists(response, data)) {
					this.showAccountExists(button, data)
					return
				}
				this.showError(
					data.error || 'Não foi possível concluir o cadastro. Tente novamente.',
					button,
				)
				return
			}

			// Cadastro completo (última etapa): conversão do Google Ads.
			if (step === '3' && typeof window.arvenneGoogleAdsSignup === 'function') {
				try {
					window.arvenneGoogleAdsSignup()
				} catch (_) {
					/* never block the signup flow on tracking */
				}
			}

			// Pixel da Meta: Lead na etapa 1, CompleteRegistration na etapa 3.
			const metaEvent = { 1: 'Lead', 3: 'CompleteRegistration' }[step]
			if (metaEvent && typeof window.arvenneMetaSignup === 'function') {
				try {
					window.arvenneMetaSignup(metaEvent, {
						customerId: data.customerId,
						email: this.val('[data-reg-email]'),
						phone: this.phoneValue(),
					})
				} catch (_) {
					/* never block the signup flow on tracking */
				}
			}

			this.goToStep(nextStep, 'push')
		} catch (_) {
			this.showError('Falha de conexão. Verifique sua internet e tente novamente.', button)
		} finally {
			this.setLoading(button, false)
		}
	}

	setLoading(button, loading) {
		this.classList.toggle('registration-form--loading', loading)
		if (button) {
			button.disabled = loading
			button.setAttribute('aria-busy', loading ? 'true' : 'false')
		}
	}

	/**
	 * Does the server response mean the e-mail already has a Shopify account?
	 * Resilient to a few shapes so the backend contract can vary:
	 *   { code: 'account_exists' } · { error_code: 'account_exists' }
	 *   { existingAccount: true }  · HTTP 409 Conflict
	 */
	isAccountExists(response, data) {
		const code = data.code || data.errorCode || data.error_code
		return (
			(response && response.status === 409) ||
			code === 'account_exists' ||
			data.existingAccount === true
		)
	}

	/**
	 * The e-mail already belongs to an account: stop the signup, explain why,
	 * and steer the user to the login shortcut at the top of the step.
	 */
	showAccountExists(button, data) {
		this.showError(
			data.error || 'Este e-mail já tem uma conta. Faça login para continuar.',
			button,
		)

		if (this.signin) {
			this.signin.classList.add('registration-form__signin--highlight')
			if (this.signinLink) {
				requestAnimationFrame(() => this.signinLink.focus({ preventScroll: true }))
			}
		}
	}

	/**
	 * Prefilled WhatsApp message identifying the lead by e-mail — the field
	 * the internal team already searches by in the Shopify admin.
	 */
	whatsappUrl() {
		const email = this.val('[data-reg-email]')
		const greeting = email
			? `Olá! Realizei meu cadastro na Arvenne (${email}) e gostaria de prosseguir com a aprovação!`
			: 'Olá! Realizei meu cadastro na Arvenne e gostaria de prosseguir com a aprovação!'
		return `https://wa.me/${RegistrationForm.WHATSAPP_NUMBER}?text=${encodeURIComponent(greeting)}`
	}

	// Seconds given to read the "Cadastro em análise" screen before the
	// auto-open fires. Generous on purpose — the audience skews older.
	static WHATSAPP_COUNTDOWN_SECONDS = 6

	/**
	 * Points the visible CTA at the prefilled message and, best-effort, auto
	 * opens WhatsApp once a visible countdown reaches zero — giving the user
	 * time to actually read the screen before the tab/app switch happens.
	 * The button stays as the reliable fallback since popup blockers (and
	 * leaving the step early, which cancels the countdown) can skip it.
	 */
	goToWhatsApp() {
		const url = this.whatsappUrl()
		if (this.whatsappLink) this.whatsappLink.href = url
		this.clearWhatsAppTimer()

		let remaining = RegistrationForm.WHATSAPP_COUNTDOWN_SECONDS
		this.renderWhatsAppCountdown(remaining)
		this.startWhatsAppProgress()

		this.whatsappTimer = window.setInterval(() => {
			remaining -= 1
			if (this.currentStep !== 'success') {
				this.clearWhatsAppTimer()
				return
			}
			if (remaining <= 0) {
				this.clearWhatsAppTimer()
				window.open(url, '_blank', 'noopener')
				return
			}
			this.renderWhatsAppCountdown(remaining)
		}, 1000)
	}

	renderWhatsAppCountdown(seconds) {
		if (!this.whatsappCountdown) return
		this.whatsappCountdown.hidden = false
		this.whatsappCountdown.textContent = `Você será redirecionado ao WhatsApp em ${seconds}s...`
	}

	/** Thin progress bar synced to the countdown via a plain CSS animation. */
	startWhatsAppProgress() {
		if (!this.whatsappProgressTrack || !this.whatsappProgressFill) return
		this.whatsappProgressTrack.hidden = false
		this.whatsappProgressFill.style.setProperty(
			'--whatsapp-countdown',
			`${RegistrationForm.WHATSAPP_COUNTDOWN_SECONDS}s`,
		)
		// Restart the CSS animation from scratch: drop the class, force a
		// reflow, then re-add it.
		this.whatsappProgressFill.classList.remove('is-running')
		void this.whatsappProgressFill.offsetWidth
		this.whatsappProgressFill.classList.add('is-running')
	}

	stopWhatsAppProgress() {
		if (!this.whatsappProgressTrack || !this.whatsappProgressFill) return
		this.whatsappProgressTrack.hidden = true
		this.whatsappProgressFill.classList.remove('is-running')
	}

	clearWhatsAppTimer() {
		if (this.whatsappTimer) window.clearInterval(this.whatsappTimer)
		this.whatsappTimer = null
		this.stopWhatsAppProgress()
		if (this.whatsappCountdown) this.whatsappCountdown.hidden = true
	}

	/** Show/clear the error box that belongs to the button's current step. */
	showError(message, button) {
		const scope = button ? button.closest('[data-reg-step]') : this
		const box = (scope || this).querySelector('[data-reg-error]')
		if (!box) return
		box.textContent = message
		box.hidden = !message
	}

	/**
	 * "Voltar": if the current step's history entry was pushed by us, real
	 * history.back() keeps URL and UI in sync (the popstate handler switches the
	 * step). Otherwise fall back to a direct switch.
	 */
	goBack(name) {
		const state = window.history.state
		if (state && state.regStep === RegistrationForm.STEP_NUMBERS[this.currentStep]) {
			window.history.back()
			return
		}
		this.goToStep(name, 'replace')
	}

	onPopstate = () => {
		const params = new URLSearchParams(window.location.search)
		const requested = parseInt(params.get(RegistrationForm.STEP_PARAM) || '1', 10) || 1
		// Never jump ahead of what the user already unlocked (e.g. the forward
		// button after a fresh load) — clamp to the furthest step reached.
		const clamped = Math.min(Math.max(requested, 1), this.maxStepReached)
		const name = RegistrationForm.STEP_NAMES[clamped]
		if (name && name !== this.currentStep) this.goToStep(name, 'none')
	}

	/**
	 * A leftover ?registration_step on a fresh load points into an empty form
	 * (the data only lives in the DOM), so strip it and start at step 1.
	 */
	normalizeStepParam() {
		const url = new URL(window.location.href)
		if (!url.searchParams.has(RegistrationForm.STEP_PARAM)) return
		url.searchParams.delete(RegistrationForm.STEP_PARAM)
		window.history.replaceState(window.history.state, '', url)
	}

	/** Mirror the active step into the URL (step 1 keeps the URL clean). */
	setStepParam(stepNumber, mode) {
		const url = new URL(window.location.href)
		if (stepNumber <= 1) url.searchParams.delete(RegistrationForm.STEP_PARAM)
		else url.searchParams.set(RegistrationForm.STEP_PARAM, String(stepNumber))
		if (mode === 'push') window.history.pushState({ regStep: stepNumber }, '', url)
		else window.history.replaceState({ regStep: stepNumber }, '', url)
	}

	/**
	 * Switch the visible step. history: 'push' adds a browser-history entry
	 * (user-initiated advance), 'replace' rewrites the current one, 'none'
	 * leaves the URL alone (popstate already changed it).
	 */
	goToStep(name, history = 'none') {
		this.steps.forEach((step) => {
			step.hidden = step.dataset.regStep !== name
			step.classList.remove('is-entering')
		})

		const active = this.querySelector(`[data-reg-step="${name}"]`)
		if (!active) return

		this.currentStep = name

		const stepNumber = RegistrationForm.STEP_NUMBERS[name]
		if (stepNumber) {
			this.maxStepReached = Math.max(this.maxStepReached, stepNumber)
			if (history !== 'none') this.setStepParam(stepNumber, history)
		} else if (name === 'success' && history !== 'none') {
			// Done — scrub the step param so a refresh lands on a clean URL.
			this.setStepParam(1, 'replace')
			this.goToWhatsApp()
		}

		// Re-trigger the entrance animation each time the step is shown
		void active.offsetWidth
		active.classList.add('is-entering')

		// Scroll back to the top of the page when switching steps.
		const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
		window.scrollTo({ top: 0, behavior })

		// Success has no field to fill, so skip the auto-focus — it would land
		// on "Voltar à loja" (the only <button> there) and show a stray focus
		// ring on a screen the user is meant to just read.
		if (name !== 'success') {
			const focusTarget = active.querySelector('input, button, [tabindex]')
			if (focusTarget) {
				// Defer focus so it doesn't fight the entrance animation/scroll reset
				requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }))
			}
		}
	}

	setFieldError(input, hasError) {
		const field = input.closest('.field')
		if (!field) return
		field.classList.toggle('field--error', hasError)
		input.setAttribute('aria-invalid', hasError ? 'true' : 'false')
	}

	clearFieldError(input) {
		const field = input.closest('.field')
		if (field) field.classList.remove('field--error')
		input.setAttribute('aria-invalid', 'false')
	}

	onlyDigits(value) {
		return (value || '').replace(/\D/g, '')
	}

	maskCPF = (value) => {
		const d = this.onlyDigits(value).slice(0, 11)
		return d
			.replace(/(\d{3})(\d)/, '$1.$2')
			.replace(/(\d{3})(\d)/, '$1.$2')
			.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
	}

	maskCNPJ = (value) => {
		const d = this.onlyDigits(value).slice(0, 14)
		return d
			.replace(/(\d{2})(\d)/, '$1.$2')
			.replace(/(\d{3})(\d)/, '$1.$2')
			.replace(/(\d{3})(\d)/, '$1/$2')
			.replace(/(\d{4})(\d{1,2})$/, '$1-$2')
	}

	maskDate = (value) => {
		const d = this.onlyDigits(value).slice(0, 8)
		if (d.length <= 2) return d
		if (d.length <= 4) return d.replace(/(\d{2})(\d+)/, '$1/$2')
		return d.replace(/(\d{2})(\d{2})(\d+)/, '$1/$2/$3')
	}

	/** Force a single leading "@" and drop spaces/extra "@" the user types. */
	maskInstagram = (value) => {
		const handle = (value || '').replace(/@/g, '').replace(/\s+/g, '')
		return '@' + handle
	}

	/** Instagram value for the payload; a bare "@" counts as empty. */
	instagramHandle() {
		if (!this.instagramInput || this.instagramInput.disabled) return ''
		const value = this.instagramInput.value.trim()
		return value === '@' ? '' : value
	}
}

// Guard against double definition in case the script ends up included twice
// on the same page (e.g. theme editor section reloads).
if (!customElements.get('registration-form')) {
	customElements.define('registration-form', RegistrationForm)
}
